import { iEdmx } from '../interfaces/iEntity';
import { iParsedEntity, iProperty } from '../interfaces/iEntityParsed';
import { initialDashboardState } from '@app/Store/reducers/dashboard.reducer';

/** Map truemetadata API types to Edm-like type strings for compatibility */
function mapTruemetadataType(apiType: string): string {
    const typeMap: Record<string, string> = {
        string: 'Edm.String',
        integer: 'Edm.Int32',
        boolean: 'Edm.Boolean',
        date: 'Edm.DateTimeOffset',
        datetimeoffset: 'Edm.DateTimeOffset',
        decimal: 'Edm.Decimal',
        double: 'Edm.Double',
    };
    return typeMap[apiType?.toLowerCase()] || `Edm.${apiType || 'String'}`;
}

/** Parse /api/truemetadata response into iParsedEntity[] */
export interface ITruemetadataTable {
    key: number;
    table: string;
    odataName: string;
    uiLabel: string;
    type: string;
    domain: string;
    masterFields: Array<{
        key: number;
        name: string;
        odataName: string;
        uiLabel: string;
        type: string;
        maxLength: number | null;
        precision: number | null;
        scale: number | null;
        readOnly: boolean;
    }>;
}

export const parseTruemetadata = (data: ITruemetadataTable[]): iParsedEntity[] => {
    if (!Array.isArray(data)) {
        throw new Error('Invalid truemetadata format: expected array');
    }

    return data.map((table) => {
        const properties: iProperty[] = table.masterFields.map((field) => ({
            name: field.odataName,
            type: mapTruemetadataType(field.type),
            nullable: true,
            maxLength: field.maxLength ?? undefined,
            readOnly: field.readOnly,
            uiLabel: field.uiLabel,
            precision: field.precision ?? undefined,
            scale: field.scale ?? undefined,
        }));

        const entityBase = table.table;
        const expectedPhysicalKey = `${entityBase.toLowerCase()}_trukey`;

        const keyField =
            table.masterFields.find((f) => f.name?.toLowerCase() === expectedPhysicalKey);
        const key = keyField ? keyField.odataName : (table.masterFields[0]?.odataName ?? 'key');

        const entity: Partial<iParsedEntity> = {
            entityName: table.uiLabel,
            key,
            properties,
            navigationProperties: [],
        };

        return applyDefaults(entity);
    });
};

const applyDefaults = (entity: Partial<iParsedEntity>): iParsedEntity => {
    const defaultEntity: iParsedEntity = {
        ...initialDashboardState.data.entities[0], // Assuming at least one entity exists in the initial state
        ...entity,
    };
    return defaultEntity;
};

export const parseEdmxData = (edmx: iEdmx): iParsedEntity[] => {
    const entityTypes = edmx['edmx:Edmx']['edmx:DataServices'].Schema.EntityType;
    if (!Array.isArray(entityTypes)) {
        throw new Error('Invalid EntityType format');
    }

    const entities: iParsedEntity[] = entityTypes.map((entityType) => {
        // Simplify property mappings to directly extract names.
        const properties = entityType.Property.map((prop) => ({
            name: prop.Name,
            type: prop.Type,
            nullable: prop.Nullable,
            maxLength: prop.MaxLength,
        }));

        // Consolidate the logic to handle navigation properties, assume it is always an array.
        const navigationProperties = (Array.isArray(entityType.NavigationProperty) ? entityType.NavigationProperty : [entityType.NavigationProperty])
            .filter((navProp) => navProp) // Filter out any possible undefined values from the array
            .map((navProp) => ({
                name: navProp.Name,
                partner: navProp.Partner,
                referentialConstraint: navProp.ReferentialConstraint
                    ? {
                          property: navProp.ReferentialConstraint.Property,
                          referencedProperty: navProp.ReferentialConstraint.ReferencedProperty,
                      }
                    : undefined,
            }));

        const entity: Partial<iParsedEntity> = {
            entityName: entityType.Name,
            key: entityType.Key.PropertyRef.Name, // Assumed that there is always a Key and PropertyRef
            properties,
            navigationProperties: navigationProperties,
        };

        return applyDefaults(entity);
    });

    return entities; // Directly return the array of iParsedEntity
};
